<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="card"
    />

    <template v-if="!isLoading">
      <!-- サマリーカード（折りたたみ） -->
      <v-expansion-panels
        v-if="recentCustomers.length > 0 || upcomingBirthdayCustomers.length > 0"
        flat
        class="mb-4 summary-panels"
      >
        <v-expansion-panel v-if="recentCustomers.length > 0">
          <v-expansion-panel-header class="py-2">
            <span class="text-subtitle-2">
              <v-icon
                small
                class="mr-1"
              >
                {{ icons.mdiAccountClock }}
              </v-icon>
              最近来店した顧客 ({{ recentCustomers.length }})
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <CustomerRecentVisits
              :customers="recentCustomers"
              @click="goToCustomerDetail"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel v-if="upcomingBirthdayCustomers.length > 0">
          <v-expansion-panel-header class="py-2">
            <span class="text-subtitle-2">
              <v-icon
                small
                class="mr-1"
              >
                {{ icons.mdiCake }}
              </v-icon>
              30日以内に誕生日の顧客 ({{ upcomingBirthdayCustomers.length }})
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <CustomerUpcomingBirthdays
              :customers="upcomingBirthdayCustomers"
              @click="goToCustomerDetail"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- 検索バー + 絞り込みボタン -->
      <div class="d-flex align-center mb-4">
        <v-text-field
          v-model="searchQuery"
          :prepend-inner-icon="icons.mdiMagnify"
          label="名前・会員番号で検索"
          single-line
          hide-details
          clearable
          dense
          outlined
          style="max-width: 400px;"
          class="mr-2"
          @input="handleSearchDebounced"
          @click:clear="handleClearSearch"
        />
        <v-btn
          outlined
          @click="filterDialog = true"
        >
          <v-icon left>
            {{ icons.mdiFilterVariant }}
          </v-icon>
          絞り込み
          <v-badge
            v-if="activeFilterCount > 0"
            :content="activeFilterCount"
            color="primary"
            inline
            class="ml-1"
          />
        </v-btn>
      </div>

      <!-- 適用中フィルターチップ -->
      <div
        v-if="activeFilterChips.length > 0"
        class="mb-4 d-flex flex-wrap"
        style="gap: 4px;"
      >
        <v-chip
          v-for="chip in activeFilterChips"
          :key="chip.key"
          small
          close
          outlined
          @click:close="removeFilter(chip.key)"
        >
          {{ chip.label }}
        </v-chip>
        <v-btn
          x-small
          text
          color="grey"
          @click="handleFilterReset"
        >
          すべて解除
        </v-btn>
      </div>

      <!-- 顧客一覧 -->
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>顧客一覧</span>
          <div>
            <v-btn
              color="secondary"
              outlined
              class="mr-2"
              :loading="downloadingCsv"
              @click="handleDownloadCsv"
            >
              <v-icon left>
                {{ icons.mdiDownload }}
              </v-icon>
              CSV
            </v-btn>
            <v-btn
              v-if="showReferralColumns"
              color="secondary"
              outlined
              class="mr-2"
              :loading="downloadingExcel"
              @click="handleDownloadExcel"
            >
              <v-icon left>
                {{ icons.mdiFileExcel }}
              </v-icon>
              Excel
            </v-btn>
            <v-btn
              color="secondary"
              outlined
              class="mr-2"
              @click="propertyManagerDialog = true"
            >
              <v-icon left>
                {{ icons.mdiPlaylistPlus }}
              </v-icon>
              プロパティ管理
            </v-btn>
            <v-btn
              color="primary"
              @click="openNewCustomerDialog"
            >
              <v-icon left>
                {{ icons.mdiPlus }}
              </v-icon>
              顧客を追加
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text>
          <CustomerList
            :customers="customers"
            :custom-fields="customFields"
            :loading="isSearching"
            :show-referral-columns="showReferralColumns"
            @click="handleCustomerClick"
            @create-table="handleCreateTableFromCustomer"
            @show-referral-tree="handleShowReferralTree"
          />
        </v-card-text>
      </v-card>
    </template>

    <!-- 絞り込みダイアログ -->
    <v-dialog
      v-model="filterDialog"
      max-width="480"
      scrollable
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>
            <v-icon class="mr-1">
              {{ icons.mdiFilterVariant }}
            </v-icon>
            絞り込み
          </span>
          <v-btn
            icon
            @click="filterDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 60vh; overflow-y: auto;">
          <CustomerFilterPanel
            ref="filterPanel"
            :customer-tags="customerTags"
            @apply="handleFilterApplyAndClose"
            @reset="handleFilterResetAndClose"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 新規顧客追加ダイアログ -->
    <v-dialog
      v-if="newCustomerDialog"
      v-model="newCustomerDialog"
      width="500"
      persistent
    >
      <CustomerForm
        :type="'newUser'"
        @submit="handleCustomerCreate"
        @cancel="newCustomerDialog = false"
      />
    </v-dialog>

    <!-- 顧客からの卓作成ダイアログ -->
    <CreateTableFromCustomerDialog
      v-model="createTableDialog"
      :customer="createTableCustomer"
      @created="handleTableCreated"
    />

    <!-- プロパティ管理ダイアログ -->
    <CustomerPropertyManagerDialog
      v-model="propertyManagerDialog"
      :custom-fields="customFields"
      @add="handleInlineAddProperty"
      @update="handleInlineUpdateProperty"
      @delete="confirmDeleteProperty"
      @reorder="handleReorderProperties"
    />

    <!-- プロパティ削除確認ダイアログ -->
    <CustomerPropertyDeleteConfirm
      v-model="deleteConfirmDialog"
      :property="deletingProperty"
      @confirm="handleDeleteProperty"
    />

    <!-- 関係性ツリーダイアログ（確定売上は絞り込みの集計期間に連動） -->
    <CustomerReferralTreeDialog
      v-model="referralTreeDialog"
      :customer="referralTreeCustomer"
      :start-date="appliedFilters.startDate || null"
      :end-date="appliedFilters.endDate || null"
    />
  </div>
</template>

<script>
import {
  getCurrentInstance,
  onMounted,
  ref,
} from '@vue/composition-api'
import {
  mdiPlus, mdiMagnify, mdiPlaylistPlus, mdiFilterVariant,
  mdiClose, mdiAccountClock, mdiCake, mdiDownload, mdiFileExcel,
} from '@mdi/js'
import { debounce } from 'lodash'
import useCompInit from '@/views/composable/useCompInit'
import useCustomers from '@/views/v2-temp/common/customer-management/composables/useCustomers'
import useCustomerCustomFields from '@/views/v2-temp/common/customer-management/composables/useCustomerCustomFields'
import useCustomerListFilters from '@/views/v2-temp/common/customer-management/composables/useCustomerListFilters'
import useCustomerListExport from '@/views/v2-temp/common/customer-management/composables/useCustomerListExport'
import CustomerForm from '@/views/v2-temp/common/customer-management/components/CustomerForm.vue'
import CustomerList from '@/views/v2-temp/common/customer-management/components/CustomerList.vue'
import CustomerFilterPanel from '@/views/v2-temp/common/customer-management/components/CustomerFilterPanel.vue'
import CustomerRecentVisits from '@/views/v2-temp/common/customer-management/components/CustomerRecentVisits.vue'
import CustomerUpcomingBirthdays from '@/views/v2-temp/common/customer-management/components/CustomerUpcomingBirthdays.vue'
import CreateTableFromCustomerDialog from '@/views/v2-temp/common/customer-management/components/CreateTableFromCustomerDialog.vue'
import CustomerPropertyDeleteConfirm from '@/views/v2-temp/common/customer-management/components/CustomerPropertyDeleteConfirm.vue'
import CustomerPropertyManagerDialog from '@/views/v2-temp/common/customer-management/components/CustomerPropertyManagerDialog.vue'
import CustomerReferralTreeDialog from '@/views/v2-temp/common/customer-management/components/CustomerReferralTreeDialog.vue'
import CustomerTagApi from '@/api/waiter/CustomerTag'

export default {
  name: 'WaiterCustomerManagement',
  components: {
    CustomerForm,
    CustomerList,
    CustomerFilterPanel,
    CustomerRecentVisits,
    CustomerUpcomingBirthdays,
    CreateTableFromCustomerDialog,
    CustomerPropertyDeleteConfirm,
    CustomerPropertyManagerDialog,
    CustomerReferralTreeDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const { initWith, isLoading } = useCompInit()

    const {
      customers,
      newCustomerDialog,
      searchQuery,
      isSearching,
      pagination,
      recentCustomers,
      upcomingBirthdayCustomers,
      getCustomers,
      searchCustomers,
      createCustomer,
      openNewCustomerDialog,
      getCustomersWithStatsFiltered,
      applyFilters,
      resetFilters,
      downloadingCsv,
      fetchCustomersCsv,
    } = useCustomers(vm)

    const {
      customFields,
      deleteConfirmDialog,
      deletingProperty,
      getCustomFields,
      addCustomField,
      updateCustomField,
      deleteCustomField,
      reorderCustomFields,
      confirmDeleteProperty,
    } = useCustomerCustomFields(vm)

    // 絞り込みUI状態（ダイアログ・チップ表示・解除操作）
    const {
      filterDialog,
      appliedFilters,
      activeFilterCount,
      activeFilterChips,
      showReferralColumns,
      removeFilter,
      handleFilterApplyAndClose,
      handleFilterResetAndClose,
      handleFilterReset,
    } = useCustomerListFilters({ applyFilters, resetFilters })

    // エクスポート（CSV / 顧客ごとシート分けの紹介売上明細Excel）
    const {
      handleDownloadCsv,
      downloadingExcel,
      handleDownloadExcel,
    } = useCustomerListExport(vm, { customers, appliedFilters, fetchCustomersCsv })

    const propertyManagerDialog = ref(false)

    // 顧客タグ一覧
    const customerTags = ref([])
    const getCustomerTags = async () => {
      try {
        const res = await CustomerTagApi.getCustomerTags()
        customerTags.value = (res.data?.customerTags?.data || []).map(t => ({
          id: t.id,
          name: t.attributes?.name || t.name,
        }))
      } catch (e) {
        // タグ取得失敗時は空のまま
      }
    }

    // 関係性ツリーダイアログ
    const referralTreeDialog = ref(false)
    const referralTreeCustomer = ref(null)

    const handleShowReferralTree = customer => {
      referralTreeCustomer.value = customer
      referralTreeDialog.value = true
    }

    // 卓作成ダイアログ
    const createTableDialog = ref(false)
    const createTableCustomer = ref(null)
    const isCreateTableMode = ref(false)

    const handleCreateTableFromCustomer = customer => {
      createTableCustomer.value = customer
      createTableDialog.value = true
    }

    const handleTableCreated = () => {
      createTableDialog.value = false
      vm.$toast.success('卓を作成しました')
      vm.$router.push({
        name: 'waiter-halls',
        params: { club_slug: vm.$route.params.club_slug },
      })
    }

    const handleCustomerCreate = async event => {
      const success = await createCustomer(event)
      if (success) {
        newCustomerDialog.value = false
      }
    }

    // インクリメンタルサーチ（300msデバウンス）
    const handleSearchDebounced = debounce(() => {
      searchCustomers(searchQuery.value, { page: 1, perPage: pagination.value.perPage })
    }, 300)

    const handleClearSearch = () => {
      searchQuery.value = ''
      getCustomers({ page: 1, perPage: pagination.value.perPage })
    }

    const goToCustomerDetail = customer => {
      vm.$router.push({
        name: 'waiter-settings-customer-management-show',
        params: {
          club_slug: vm.$route.params.club_slug,
          customerId: customer.id,
        },
      })
    }

    // 顧客クリック: create_table モード時はダイアログ、通常は詳細
    const handleCustomerClick = customer => {
      if (isCreateTableMode.value) {
        handleCreateTableFromCustomer(customer)
      } else {
        goToCustomerDetail(customer)
      }
    }

    const handleInlineAddProperty = event => {
      addCustomField({ ...event, callback: () => {} })
    }

    const handleInlineUpdateProperty = event => {
      updateCustomField(event.id, { name: event.name, callback: () => {} })
    }

    const handleDeleteProperty = event => {
      deleteCustomField(deletingProperty.value.id, event)
    }

    const handleReorderProperties = ids => {
      reorderCustomFields(ids)
    }

    onMounted(() => {
      // create_table モードの検出
      isCreateTableMode.value = vm.$route.query.mode === 'create_table'

      initWith([
        getCustomers(),
        getCustomFields(),
        getCustomerTags(),
        getCustomersWithStatsFiltered(),
      ])
    })

    return {
      isLoading,
      isSearching,
      customers,
      newCustomerDialog,
      searchQuery,
      customFields,
      customerTags,
      recentCustomers,
      upcomingBirthdayCustomers,
      createTableDialog,
      createTableCustomer,
      filterDialog,
      appliedFilters,
      activeFilterCount,
      activeFilterChips,
      showReferralColumns,
      referralTreeDialog,
      referralTreeCustomer,
      handleShowReferralTree,
      downloadingCsv,
      handleDownloadCsv,
      downloadingExcel,
      handleDownloadExcel,
      deleteConfirmDialog,
      deletingProperty,
      propertyManagerDialog,
      openNewCustomerDialog,
      confirmDeleteProperty,
      handleCustomerCreate,
      handleInlineAddProperty,
      handleInlineUpdateProperty,
      handleDeleteProperty,
      handleReorderProperties,
      handleSearchDebounced,
      handleClearSearch,
      handleFilterApplyAndClose,
      handleFilterResetAndClose,
      handleFilterReset,
      handleCustomerClick,
      handleCreateTableFromCustomer,
      handleTableCreated,
      removeFilter,
      goToCustomerDetail,
      icons: {
        mdiPlus,
        mdiMagnify,
        mdiPlaylistPlus,
        mdiFilterVariant,
        mdiClose,
        mdiAccountClock,
        mdiCake,
        mdiDownload,
        mdiFileExcel,
      },
    }
  },
}
</script>

<style scoped>
.summary-panels {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
</style>
